import React from "react";
import { useFormik } from "formik";
import * as Yup from "yup";
import { useNavigate } from "react-router-dom";
import { Helmet } from "react-helmet";

import * as Cognito from "providers/cognito";
import * as User from "providers/user";
import * as Axios from "providers/axios";
import { Button, BUTTON_SIZES } from "components/Button";
import AuthInput from "components/AuthInput";
import AuthLayout from "components/AuthLayout";
import { authSubmitButtonStyles } from "utils/constants";

export default function UserName() {
  const navigate = useNavigate();
  const { user: cognitoUser } = Cognito.useCognito();
  const { user, updateUser } = User.useUser();
  const { api } = Axios.useAxios();
  const [submitting, setSubmitting] = React.useState<boolean>(false);
  const [error, setError] = React.useState<string>("");

  type FormValues = {
    email: string;
    firstName: string;
    lastName: string;
  };

  React.useEffect(() => {
    if (user.first_name && user.last_name) {
      if (user.joined_company_id) {
        navigate(`/dashboard/User/${user.id}`);
        return;
      }
      navigate(
        !user.company && !user.joined_company_id
          ? "/on-boarding/company"
          : `/dashboard`
      );
      return;
    }
  });

  const { handleSubmit, handleChange, touched, errors, values } = useFormik({
    initialValues: {
      email: cognitoUser?.email || "",
      firstName: "",
      lastName: ""
    },
    validationSchema: UserNameSchema,
    validateOnChange: true,
    validateOnMount: false,
    validateOnBlur: false,
    onSubmit: async (values: FormValues) => {
      try {
        setSubmitting(true);

        const { data } = await api.post(`/v1/users`, {
          ...values,
          sub: cognitoUser?.id
        });

        updateUser(data);

        if (data.id && !data.company && !data.joined_company) {
          return navigate(`/on-boarding/company`);
        }

        return navigate(`/dashboard`);
      } catch (error: any) {
        setError(error.message);
      } finally {
        setSubmitting(false);
      }
    }
  });

  return (
    <>
      <Helmet>
        <title>Pointment | On-boarding | Your name</title>
        <meta property="og:title" content="Pointment | Authenticate" />
      </Helmet>
      <AuthLayout title="Your name" error={error} setError={setError}>
        <form onSubmit={handleSubmit}>
          <AuthInput
            type="text"
            name="firstName"
            label="First name"
            placeholder="First name"
            value={values.firstName}
            onChange={handleChange}
            touched={touched}
            errors={errors}
            autocapitalize="none"
          />
          <AuthInput
            type="text"
            name="lastName"
            label="Last name"
            placeholder="Last name"
            value={values.lastName}
            onChange={handleChange}
            touched={touched}
            errors={errors}
            autocapitalize="none"
          />
          <div className="flex lg:flex-row flex-col justify-between lg:items-center">
            <Button
              className={`${authSubmitButtonStyles} !w-full`}
              text={"Save"}
              type="submit"
              size={BUTTON_SIZES.MEDIUM}
              submitting={submitting}
              disabledOnSubmit
            />
          </div>
        </form>
      </AuthLayout>
    </>
  );
}

const UserNameSchema = Yup.object().shape({
  firstName: Yup.string().required("This field is required"),
  lastName: Yup.string().required("This field is required")
});
